// EditRecordBankSection.jsx — 계좌·세금정보 연결 모달의 계좌정보 섹션 (EditRecordModal에서 분리)
import React, { useMemo, useState } from 'react';
import { todayKST } from '../../../utils/date';
import SingleDatePicker from '../../../components/SingleDatePicker';
import {
  accountLabel, getItemStatus, canAutoSelectNewInfo, changeModeForDate,
  sectionStyle, titleStyle, hintStyle, inputStyle, gridStyle, smallBtnStyle,
  primaryBtnStyle, dangerBtnStyle, statusBadgeStyle, optionMetaStyle,
  optionDescriptionStyle, errorStyle, effectiveDateBadgeStyle, datePanelStyle,
  autocompleteWrapStyle, dropdownStyle, dropdownItemStyle,
} from './editRecordModal.shared';

export function EditRecordBankSection({
  record,
  activeAccounts,
  effectiveBankId,
  settlementDateYmd,
  selBank,
  setSelBank,
  bankCodes,
  onAddBankAccount,
  onDeleteBankAccount,
}) {
  const today = todayKST();
  const [addingBank, setAddingBank] = useState(false);
  const [bankCodeId, setBankCodeId] = useState('');
  const [bankQuery, setBankQuery] = useState('');
  const [showBankDrop, setShowBankDrop] = useState(false);
  const [acctNum, setAcctNum] = useState('');
  const [acctHolder, setAcctHolder] = useState('');
  const [bankEffectiveFrom, setBankEffectiveFrom] = useState(today);
  const [bankReason, setBankReason] = useState('');
  const [bankErr, setBankErr] = useState('');
  const [bankSaving, setBankSaving] = useState(false);

  const filteredBanks = useMemo(() => {
    const q = bankQuery.trim().toLowerCase();
    if (!q) return bankCodes;
    return bankCodes.filter((b) => `${b.code || ''} ${b.name || ''}`.toLowerCase().includes(q));
  }, [bankCodes, bankQuery]);

  const pickBank = (bank) => {
    if (!bank) return;
    setBankCodeId(bank.id);
    setBankQuery(bank.name || '');
    setShowBankDrop(false);
    setBankErr('');
  };
  const resolveBankCodeId = () => {
    if (bankCodeId) return bankCodeId;
    const q = bankQuery.trim().toLowerCase();
    if (!q) return '';
    const exact = bankCodes.find((b) => {
      const code = String(b.code || '').toLowerCase();
      const name = String(b.name || '').toLowerCase();
      return q === code || q === name || q === `${code} ${name}`.trim();
    });
    return exact?.id || '';
  };
  const handleAddBank = async () => {
    setBankErr('');
    const resolvedBankCodeId = resolveBankCodeId();
    if (!resolvedBankCodeId) { setBankErr('은행을 검색해서 선택하세요.'); return; }
    if (!acctNum.trim()) { setBankErr('계좌번호를 입력하세요.'); return; }
    if (!acctHolder.trim()) { setBankErr('예금주를 입력하세요.'); return; }
    if (!bankEffectiveFrom) { setBankErr('적용일을 선택하세요.'); return; }
    setBankSaving(true);
    try {
      const newId = await onAddBankAccount(
        record.user_id,
        Number(resolvedBankCodeId),
        acctNum.trim(),
        acctHolder.trim(),
        {
          effectiveFrom: bankEffectiveFrom,
          changeMode: changeModeForDate(bankEffectiveFrom),
          reason: bankReason.trim(),
        },
      );
      if (newId) {
        if (canAutoSelectNewInfo(bankEffectiveFrom, activeAccounts, settlementDateYmd)) setSelBank(newId);
        setAddingBank(false);
        setBankCodeId('');
        setBankQuery('');
        setAcctNum('');
        setAcctHolder('');
        setBankReason('');
      }
    } finally {
      setBankSaving(false);
    }
  };
  const handleDeleteBank = (id) => {
    if (!window.confirm('이 계좌정보를 비활성화할까요? 이미 연결된 정산 기록의 스냅샷은 유지됩니다.')) return;
    if (String(selBank) === String(id)) setSelBank('');
    onDeleteBankAccount(id, record.user_id);
  };

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={titleStyle}>계좌정보</div>
          <div style={hintStyle}>정산일 기준 적용 계좌를 선택하거나 새 계좌를 등록합니다.</div>
        </div>
        <button style={smallBtnStyle} onClick={() => { setAddingBank((v) => !v); setBankErr(''); }}>
          {addingBank ? '닫기' : '+ 계좌 등록'}
        </button>
      </div>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
        <input type="radio" name="selBank" checked={!selBank} onChange={() => setSelBank('')} />
        선택 안 함
      </label>
      {activeAccounts.map((b) => {
        const status = getItemStatus(b, effectiveBankId, settlementDateYmd);
        const disabled = !status.selectable;
        return (
        <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, fontSize: 12, color: 'var(--text)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }}>
            <input type="radio" name="selBank" checked={String(selBank) === String(b.id)} disabled={disabled} onChange={() => setSelBank(b.id)} />
            <span style={optionMetaStyle}>
              <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{accountLabel(b)}</span>
              <span style={statusBadgeStyle(status.tone)}>{status.label}</span>
              {status.description && <span style={optionDescriptionStyle}>{status.description}</span>}
            </span>
          </label>
          <button style={dangerBtnStyle} onClick={() => handleDeleteBank(b.id)}>삭제</button>
        </div>
        );
      })}
      {activeAccounts.length === 0 && <div style={hintStyle}>등록된 계좌정보가 없습니다.</div>}

      {addingBank && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
          <div style={gridStyle}>
            <div style={autocompleteWrapStyle}>
              <input
                style={inputStyle}
                value={bankQuery}
                onChange={(e) => {
                  setBankQuery(e.target.value);
                  setBankCodeId('');
                  setShowBankDrop(true);
                }}
                onFocus={() => setShowBankDrop(true)}
                onBlur={() => setTimeout(() => setShowBankDrop(false), 150)}
                placeholder="은행 검색 (코드 또는 이름)"
                autoComplete="off"
              />
              {showBankDrop && filteredBanks.length > 0 && (
                <div style={dropdownStyle}>
                  {filteredBanks.map((b) => (
                    <div
                      key={b.id}
                      style={dropdownItemStyle}
                      onMouseDown={() => pickBank(b)}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-card)'; }}
                    >
                      <span style={{ fontWeight: 800, marginRight: 6 }}>{b.code}</span>
                      {b.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={effectiveDateBadgeStyle}>적용 시작일 {bankEffectiveFrom}</div>
          </div>
          <div style={datePanelStyle}>
            <div style={hintStyle}>달력에서 날짜를 누르면 그 날짜부터 이 계좌정보가 적용됩니다.</div>
            <SingleDatePicker value={bankEffectiveFrom} onChange={setBankEffectiveFrom} />
          </div>
          <div style={gridStyle}>
            <input style={inputStyle} placeholder="계좌번호" value={acctNum} onChange={(e) => setAcctNum(e.target.value)} />
            <input style={inputStyle} placeholder="예금주" value={acctHolder} onChange={(e) => setAcctHolder(e.target.value)} />
          </div>
          <input style={inputStyle} placeholder="변경 사유 또는 메모" value={bankReason} onChange={(e) => setBankReason(e.target.value)} />
          {bankErr && <span style={errorStyle}>{bankErr}</span>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={primaryBtnStyle} onClick={handleAddBank} disabled={bankSaving}>
              {bankSaving ? '등록 중...' : '등록'}
            </button>
            <button style={smallBtnStyle} onClick={() => setAddingBank(false)}>취소</button>
          </div>
        </div>
      )}
    </div>
  );
}
